import React from "react";

import { cn } from "../../../lib/utils";

// context
import { useInputField } from "../../molecules/InputWrapper/InputContext";
// components
import { InputBase, InputBaseProps } from "../InputBase";

type InputTextProps = Omit<InputBaseProps, "type" | "min" | "max" | "inputMode">;

/**
 * InputText is an Isotope of InputBase with additional props for text input.
 * It is a controlled component that uses the value and onChange props to update the input value.
 * This component is used outside of a form context. If you need a form-connected text input, use FormInputText
 */
const InputText = ({
	size,
	value,
	onChange,
	onBlur,
	onKeyDown,
	disabled,
	errored,
	dangerouslySetClassName,
	id,
	"data-testid": dataTestId,
	"aria-label": ariaLabel,
	leftIcon,
	placeholder,
	clearable,
	minLength,
	maxLength,
	spellCheck,
}: InputTextProps) => {
	// If inside an InputWrapper with a label, the for/id association handles
	// accessibility — don't set aria-label. Only fall back to placeholder when
	// there's no associated label (no InputWrapper context).
	const { id: contextId } = useInputField();
	const hasAssociatedLabel = !!contextId;
	// Safety net: when no visible label or explicit ariaLabel exists, derive from
	// placeholder with trailing ellipsis stripped. Prefer explicit ariaLabel prop.
	const resolvedAriaLabel =
		ariaLabel || (!hasAssociatedLabel ? placeholder?.replace(/\.{2,}$/, "").trim() : undefined);

	return (
		<InputBase
			size={size}
			value={value}
			onChange={onChange}
			onBlur={onBlur}
			onKeyDown={onKeyDown}
			type="text"
			inputMode="text"
			disabled={disabled}
			errored={errored}
			dangerouslySetClassName={cn(dangerouslySetClassName)}
			leftIcon={leftIcon}
			placeholder={placeholder}
			clearable={clearable}
			id={id}
			data-testid={dataTestId}
			aria-label={resolvedAriaLabel}
			minLength={minLength}
			maxLength={maxLength}
			spellCheck={spellCheck}
		/>
	);
};

export { InputText, type InputTextProps };
